
import React from 'react';
import { BookOpen, Sparkles, User, Store, ShieldCheck, Zap, HelpCircle, ChevronRight, CheckCircle, MessageSquare, Calendar, Banknote } from 'lucide-react';
// Corrected import path for useTranslation
import { useTranslation } from '../LanguageContext';
import { Language } from '../types';

export const CheangManual: React.FC = () => {
  const { language } = useTranslation();

  const content: Record<string, Record<Language, string>> = {
    title: { 
      EN: 'User Guide & Manual', 
      KH: 'សៀវភៅណែនាំប្រើប្រាស់', 
      ZH: '用户指南与手册', 
      JA: 'ユーザーガイド＆マニュアル', 
      KO: '사용자 가이드 및 매뉴얼' 
    },
    sub: { 
      EN: 'Master the Cheang platform with these detailed instructions.', 
      KH: 'ស្វែងយល់ពីរបៀបប្រើប្រាស់កម្មវិធី Cheang ឱ្យកាន់តែប្រសើរឡើង។', 
      ZH: '通过这些详细说明掌握 Cheang 平台。', 
      JA: '詳細な手順に従って Cheang プラットフォームを使いこなしましょう。', 
      KO: '상세한 지침에 따라 Cheang 플랫폼을 숙달하십시오.' 
    },
    intro: {
      EN: 'Welcome to Cheang, the smart repair marketplace. Our platform bridges the gap between expert technicians and people who need quality fixes.',
      KH: 'សូមស្វាគមន៍មកកាន់ Cheang ដែលជាទីផ្សារជួសជុលឆ្លាតវៃ។ កម្មវិធីរបស់យើងភ្ជាប់ទំនាក់ទំនងរវាងជាងជំនាញ និងអ្នកដែលត្រូវការសេវាកម្មជួសជុលប្រកបដោយគុណភាព។',
      ZH: '欢迎来到智能维修市场 Cheang。我们的平台搭建了专家技师与需要优质维修的人们之间的桥梁。',
      JA: 'スマート修理マーケットプレイス「Cheang」へようこそ。当プラットフォームは、専門技術者と質の高い修理を必要とする人々をつなぎます。',
      KO: '스마트 수리 마켓플레이스 Cheang에 오신 것을 환영합니다. 당사 플랫폼은 전문 기술자와 고품질 수리가 필요한 사람들을 연결합니다.'
    }
  };

  const sections = [
    {
      icon: User,
      color: 'text-violet-600',
      bg: 'bg-violet-50',
      title: { 
        EN: 'For Customers', 
        KH: 'សម្រាប់អតិថិជន', 
        ZH: '面向客户', 
        JA: '顧客向け', 
        KO: '고객용' 
      },
      steps: [
        {
          head: { EN: 'Visual Search', KH: 'ការស្វែងរកតាមរូបភាព', ZH: '视觉搜索', JA: '画像検索', KO: '시각적 검색' },
          desc: { 
            EN: 'Use the AI Visual Diagnosis button on the home screen. Snap a photo of your broken device, and our AI will identify the issue and category automatically.', 
            KH: 'ប្រើប៊ូតុង "រោគវិនិច្ឆ័យតាមរូបភាព" នៅលើទំព័រដើម។ ថតរូបឧបករណ៍ដែលខូច ហើយ AI នឹងកំណត់បញ្ហា និងប្រភេទសេវាកម្មដោយស្វ័យប្រវត្តិ។',
            ZH: '使用主屏幕上的 AI 视觉诊断按钮。拍摄故障设备的照片，我们的 AI 将自动识别问题和类别。',
            JA: 'ホーム画面の AI 画像診断ボタンを使用してください。故障したデバイスの写真を撮ると、AI が自動的に問題とカテゴリを特定します。',
            KO: '홈 화면에서 AI 시각 진단 버튼을 사용하십시오. 고장 난 기기의 사진을 찍으면 AI가 자동으로 문제와 카테고리를 식별합니다.'
          }
        },
        {
          head: { EN: 'AI Diagnosis', KH: 'រោគវិនិច្ឆ័យដោយ AI', ZH: 'AI 诊断', JA: 'AI 診断', KO: 'AI 진단' },
          desc: { 
            EN: 'When booking a shop, provide a detailed description. Our system generates a technical assessment and a price estimate in Cambodian Riel to ensure transparency.', 
            KH: 'នៅពេលកក់សេវាកម្ម សូមរៀបរាប់ពីបញ្ហាឱ្យបានច្បាស់លាស់។ ប្រព័ន្ធរបស់យើងនឹងបង្កើតការវាយតម្លៃបច្ចេកទេស និងតម្លៃប៉ាន់ស្មានជាប្រាក់រៀល ដើម្បីធានាតម្លាភាព។',
            ZH: '预约店铺时，请提供详细描述。我们的系统会生成技术评估和柬埔寨瑞尔的价格估算，以确保透明度。',
            JA: 'ショップを予約する際は、詳細な説明を入力してください。システムが技術評価とカンボジアリエルでの見積価格を生成し、透明性を確保します。',
            KO: '상점을 예약할 때 자세한 설명을 제공하십시오. 당사 시스템은 투명성을 보장하기 위해 기술 평가와 캄보디아 리엘 기준 견적을 생성합니다.'
          }
        },
        {
          head: { EN: 'Maintenance Reminders', KH: 'ការរំលឹកការថែទាំ', ZH: '维护提醒', JA: 'メンテナンスリマインダー', KO: '유지 관리 알림' },
          desc: { 
            EN: 'Add reminders manually or use AI Smart Add. For example, type "I bought a TV with 2 years warranty," and the system will set a notification before it expires.', 
            KH: 'បន្ថែមការរំលឹកដោយខ្លួនឯង ឬប្រើ AI Smart Add។ ឧទាហរណ៍៖ វាយថា "ខ្ញុំទិញទូរទស្សន៍ដែលមានធានា ២ ឆ្នាំ" ហើយប្រព័ន្ធនឹងកំណត់ការជូនដំណឹងមុនពេលផុតកំណត់។',
            ZH: '手动添加提醒或使用 AI 智能添加。例如，输入“我买了一台保修两年的电视”，系统会在保修期满前设置通知。',
            JA: '手動でリマインダーを追加するか、AI スマート追加を使用します。例えば「2年保証付きのテレビを購入した」と入力すると、期限が切れる前に通知が設定されます。',
            KO: '수동으로 알림을 추가하거나 AI 스마트 추가를 사용하십시오. 예를 들어 "2년 보증 TV를 구매했습니다"라고 입력하면 보증 만료 전에 시스템이 알림을 설정합니다.'
          }
        }
      ]
    },
    {
      icon: Store,
      color: 'text-emerald-600',
      bg: 'bg-emerald-50',
      title: { 
        EN: 'For Shop Owners', 
        KH: 'សម្រាប់ម្ចាស់ហាង', 
        ZH: '面向店主', 
        JA: '店主向け', 
        KO: '상점 주인용' 
      },
      steps: [
        {
          head: { EN: 'Catalog & AI Images', KH: 'សេវាកម្ម និងរូបភាព AI', ZH: '目录与 AI 图像', JA: 'カタログと AI 画像', KO: '카탈로그 및 AI 이미지' },
          desc: { 
            EN: 'Create service packages and use the "AI Generate" button to create professional cover photos for your offerings automatically.', 
            KH: 'បង្កើតកញ្ចប់សេវាកម្ម និងប្រើប៊ូតុង "AI Generate" ដើម្បីបង្កើតរូបភាពសេវាកម្មប្រកបដោយវិជ្ជាជីវៈដោយស្វ័យប្រវត្តិ។',
            ZH: '创建服务包，并使用“AI 生成”按钮自动为您提供的服务创建专业封面照片。',
            JA: 'サービスパッケージを作成し、「AI 生成」ボタンを使用して、提供するサービスのプロフェッショナルなカバー写真を自動的に作成します。',
            KO: '서비스 패키지를 생성하고 "AI 생성" 버튼을 사용하여 제공하는 서비스의 전문 커버 사진을 자동으로 생성하십시오.'
          }
        },
        {
          head: { EN: 'Task Logging', KH: 'ការកត់ត្រាការងារ', ZH: '任务日志', JA: 'タスクログ', KO: '작업 로그' },
          desc: { 
            EN: 'Your technical team can log individual work tasks during a repair. These logs are used to generate accurate, itemized invoices later.', 
            KH: 'ក្រុមបច្ចេកទេសអាចកត់ត្រារាល់សកម្មភាពការងារក្នុងអំឡុងពេលជួសជុល។ កំណត់ត្រាទាំងនេះនឹងត្រូវប្រើដើម្បីបង្កើតវិក្កយបត្រលម្អិតនៅពេលក្រោយ។',
            ZH: '您的技术团队可以在维修过程中记录各项工作任务。这些日志稍后将用于生成准确的项目清单发票。',
            JA: '技術チームは修理中に個々の作業タスクを記録できます。これらのログは、後で正確な内訳付き請求書を作成するために使用されます。',
            KO: '기술 팀은 수리 중에 개별 작업 작업을 기록할 수 있습니다. 이 로그는 나중에 정확한 항목별 인보이스를 생성하는 데 사용됩니다.'
          }
        },
        {
          head: { EN: 'AI Invoicing', KH: 'ការបង្កើតវិក្កយបត្រដោយ AI', ZH: 'AI 发票开具', JA: 'AI 請求書作成', KO: 'AI 인보이스 생성' },
          desc: { 
            EN: 'Once repair tasks are finished, click "Generate AI Invoice." Our AI reads the technical logs and formats a professional bill for the customer.', 
            KH: 'នៅពេលការជួសជុលរួចរាល់ សូមចុច "Generate AI Invoice"។ AI នឹងអានកំណត់ត្រាបច្ចេកទេស និងរៀបចំវិក្កយបត្រដែលមានលក្ខណៈស្តង់ដារសម្រាប់អតិថិជន។',
            ZH: '维修任务完成后，点击“生成 AI 发票”。我们的 AI 会读取技术日志，并为客户格式化专业账单。',
            JA: '修理タスクが完了したら、「AI 請求書作成」をクリックしてください。AI が技術ログを読み取り、顧客向けのプロフェッショナルな請求書を作成します。',
            KO: '수리 작업이 완료되면 "AI 인보이스 생성"을 클릭하십시오. 당사 AI가 기술 로그를 읽고 고객을 위한 전문적인 청구서를 작성합니다.'
          }
        }
      ]
    }
  ];

  return (
    <div className="pb-32 pt-24 md:pt-8 px-4 md:px-12 max-w-5xl mx-auto animate-in fade-in duration-500">
      {/* Hero Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-8">
        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 bg-violet-50 px-4 py-2 rounded-2xl border border-violet-100 shadow-sm">
             <BookOpen size={16} className="text-violet-600" />
             <span className="text-[10px] font-black text-violet-600 uppercase tracking-widest">Documentation</span>
          </div>
          <h1 className="text-5xl font-black text-slate-900 tracking-tighter">{content.title[language]}</h1>
          <p className="text-slate-500 font-bold text-xl max-w-2xl">{content.sub[language]}</p>
        </div>
        <div className="w-16 h-16 md:w-24 md:h-24 bg-white rounded-3xl border border-slate-100 flex items-center justify-center text-slate-300 shadow-2xl shrink-0">
           <HelpCircle size={48} className="opacity-20" />
        </div>
      </div>

      <div className="prose prose-slate max-w-none mb-16">
         <p className="text-lg text-slate-600 leading-relaxed font-medium">
           {content.intro[language]}
         </p>
      </div>

      <div className="space-y-20">
        {sections.map((section, idx) => (
          <div key={idx} className="relative">
             <div className="flex items-center gap-6 mb-12">
                <div className={`w-14 h-14 ${section.bg} ${section.color} rounded-2xl flex items-center justify-center shadow-lg`}>
                   <section.icon size={28} />
                </div>
                <h2 className="text-3xl font-black text-slate-900 tracking-tight">{section.title[language]}</h2>
             </div>

             <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {section.steps.map((step, sIdx) => (
                  <div key={sIdx} className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-500 group">
                     <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center text-slate-400 font-black text-xs mb-6 group-hover:bg-slate-900 group-hover:text-white transition-colors">
                        0{sIdx + 1}
                     </div>
                     <h3 className="text-xl font-black text-slate-900 mb-4 tracking-tight">{step.head[language]}</h3>
                     <p className="text-slate-500 font-medium text-sm leading-relaxed">
                        {step.desc[language]}
                     </p>
                  </div>
                ))}
             </div>
          </div>
        ))}
      </div>

      {/* Quick FAQ / Tools Footer */}
      <div className="mt-24 p-10 md:p-16 bg-slate-900 rounded-[3.5rem] text-white shadow-2xl relative overflow-hidden group">
         <div className="absolute top-0 right-0 p-12 opacity-10 group-hover:scale-110 transition-transform"><Zap size={128} /></div>
         <div className="relative z-10 max-w-2xl">
            <h2 className="text-3xl font-black mb-6 tracking-tighter">
               {language === 'KH' ? 'តើអ្នកនៅតែមានចម្ងល់មែនទេ?' : 
                language === 'EN' ? 'Still have questions?' : 
                '仍然有疑问吗？'}
            </h2>
            <p className="text-slate-400 font-bold text-lg mb-10 leading-relaxed">
               {language === 'KH' ? 'ក្រុមការងារបច្ចេកទេសរបស់យើងត្រៀមខ្លួនជួយអ្នកជានិច្ច។ សូមទាក់ទងមកយើងខ្ញុំតាមរយៈ Telegram ឬអ៊ីមែលសម្រាប់ជំនួយបន្ថែម។' : 
                language === 'EN' ? 'Our technical support team is always ready to assist. Contact us via Telegram or email for further help.' : 
                '我们的技术支持团队随时为您提供帮助。'}
            </p>
            <div className="flex flex-wrap gap-4">
               <button className="px-8 py-5 bg-violet-600 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-violet-700/30 hover:bg-violet-500 transition-all flex items-center gap-3">
                  <MessageSquare size={18} /> Contact Support
               </button>
               <button className="px-8 py-5 bg-white/5 border border-white/10 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-white/10 transition-all">
                  Visit FAQ Center
               </button>
            </div>
         </div>
      </div>

      <div className="mt-20 pt-10 border-t border-slate-100 text-center">
         <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.4em]">Cheang with Fe - Documentation v1.0.0</p>
      </div>
    </div>
  );
};
